import { PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { useMessageBillingUsage } from '@community_dev/hooks'
import { AudioPlayer, COLORS, MultimediaInput, composeValidators, required } from '@community_dev/pixels'
import { Dropzone, Layout } from '@community_dev/pixels'
import { OnboardingMessageTemplateType } from '@community_dev/types/lib/api/OnboardingMessages'
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import { Button, Checkbox, Modal, Select, Typography, notification } from 'antd'
import { DefaultOptionType } from 'antd/es/select'
import keyBy from 'lodash/keyBy'
import lowerCase from 'lodash/lowerCase'
import startCase from 'lodash/startCase'
import { Dispatch, SetStateAction, useCallback, useMemo, useState, type JSX } from 'react'
import { Field, Form, useForm } from 'react-final-form'

import { useChopperDomain } from '../hooks/useChopperDomain'
import { SettingsCard, SettingsPage, SettingsRow, SettingsRows } from '../Settings'

import { EditOnboardingMessagesList } from './components'
import { EDITABLE_MESSAGES, ONBOARDING_MESSAGE_TITLES } from './constants'

import { Client, getOnboardingMessageLabels, postImportOnboardingMessage, putClient, putVoicemail } from 'api/clients'
import { QUERIES } from 'constants/queries'
import { Switch } from 'shared/components/Switch'
import { TemplateTypeSelect } from 'shared/components/TemplateTypeSelect/TemplateTypeSelect'

function MessageField({
  chopperDomain,
  name,
  validators,
  label,
  shouldShortenLinks,
  setShouldShortenLinks,
}: {
  chopperDomain: string
  name: string
  validators: any[]
  label: string
  shouldShortenLinks: boolean
  setShouldShortenLinks: Dispatch<SetStateAction<boolean>>
}): JSX.Element {
  const { getFieldState } = useForm()
  const message = getFieldState(name)?.value
  const { charCount, error, segmentCount, segmentMessage, tooltipContent } = useMessageBillingUsage({
    chopperDomain,
    message,
    shouldShortenLinks,
  })
  const usageCounterProps = {
    charCount,
    error,
    segmentCount,
    segmentMessage,
    tooltipContent,
  }
  return (
    <Field name="text" validate={composeValidators(...validators)}>
      {({ input, meta }) => (
        <>
          <label htmlFor="text" style={{ display: 'block', marginTop: '8px' }}>
            {label}
            <MultimediaInput
              gallery={false}
              gif={false}
              onEmojiSelect={(_, text) => {
                input.onChange(text)
              }}
              placeholder=""
              usageCounterProps={usageCounterProps}
              {...input}
            />
          </label>
          {!meta.active && meta.touched && meta.modified && meta.error && (
            <Typography.Text type="danger">
              {label} {meta.error}
            </Typography.Text>
          )}
          <Checkbox
            checked={shouldShortenLinks}
            onChange={(e) => setShouldShortenLinks(e.target.checked)}
            style={{ marginTop: '12px', fontSize: '12px', color: COLORS.TEXT }}
          >
            Use Community Link Shortener
          </Checkbox>
        </>
      )}
    </Field>
  )
}

export function Messages({ client }: { client: Client }): JSX.Element {
  const queryClient = useQueryClient()
  const chopperDomain = useChopperDomain(client.id, client.whiteLabelEnabled ?? false)
  const [isCreateImportOnboardingMessageModalOpen, setIsCreateImportOnboardingMessageModalOpen] = useState(false)
  const [shouldShortenLinks, setShouldShortenLinks] = useState(true)
  function showError(message: any) {
    notification.error({
      message,
      placement: 'bottomRight',
      duration: 2,
    })
  }

  function showSuccess(message: any) {
    notification.success({
      message,
      placement: 'bottomRight',
      duration: 2,
    })
  }

  const clientMessageByLabel = useMemo(() => keyBy(client.onboardingMessages, 'label'), [client.onboardingMessages])

  const { data: onboardingMessageLabelOptions } = useQuery(
    [QUERIES.CLIENT_ONBOARDING_LABELS, { clientId: client.id }],
    {
      queryFn: () => getOnboardingMessageLabels(client.id),
      select: ({ data: labels }): DefaultOptionType[] => {
        return (
          labels
            .map(({ label }) => {
              const used = clientMessageByLabel[label] !== undefined
              return {
                label: (
                  <>
                    {ONBOARDING_MESSAGE_TITLES[label] || startCase(lowerCase(label))} {used && <i> — already exists</i>}
                  </>
                ),
                value: label,
                disabled: used,
              }
            })
            // disabled options at the bottom
            .sort((a) => (a.disabled ? 1 : -1))
        )
      },
    },
  )
  const { mutate: saveVoicemail } = useMutation(putVoicemail, {
    onSuccess() {
      showSuccess('Voicemail saved.')
      queryClient.invalidateQueries([QUERIES.CLIENT, { id: client.id }])
    },
    onError() {
      showError('Voicemail failed to upload. Please try again.')
    },
  })
  const { mutateAsync: createImportOnboardingMessage } = useMutation({
    mutationFn: ({
      text,
      label,
      templateType,
    }: {
      text: string
      label: string
      templateType?: OnboardingMessageTemplateType
    }) => {
      return postImportOnboardingMessage({
        clientId: client.id,
        text,
        label,
        templateType,
        shortenLinks: shouldShortenLinks,
      })
    },
    onError: () => {
      notification.error({
        message: 'Could not create import onboarding message',
      })
    },
    onSuccess: () => {
      notification.success({
        message: 'Import onboarding message created',
      })
      setIsCreateImportOnboardingMessageModalOpen(false)
      queryClient.invalidateQueries([QUERIES.CLIENT, { id: client.id }])
    },
  })
  const isWhatsAppMessageLabel = useCallback(
    (label: string | undefined) =>
      label?.toLocaleLowerCase().includes('whatsapp') && label.toLocaleLowerCase().includes('import'),
    [],
  )

  function invalidateClient() {
    queryClient.invalidateQueries([QUERIES.CLIENT, { id: client.id }])
  }

  function onFileAccepted(file: File) {
    const body = new FormData()
    body.append('voicemail', file)
    saveVoicemail({ id: client.id, body })
  }

  function handleCreateImportOnboardingMessage({
    label,
    text,
    templateType,
  }: {
    label: string
    text: string
    templateType?: OnboardingMessageTemplateType
  }) {
    return createImportOnboardingMessage({
      label,
      text,
      // only include the template type when it’s required.
      templateType: isWhatsAppMessageLabel(label) ? templateType : undefined,
    })
  }

  return (
    <SettingsPage description="Onboarding and import messages for this client." title="Messages">
      <SettingsCard title="Messaging Options">
        <SettingsRows>
          <SettingsRow label="Auto Drip">
            <Switch
              defaultChecked={client.autoDripEnabled}
              name="Auto Drip"
              onSuccess={invalidateClient}
              prepare={(value) => ({
                id: client.id,
                body: { auto_drip_enabled: value },
              })}
              updateEntity={putClient}
            />
          </SettingsRow>
          <SettingsRow label="International">
            <Switch
              defaultChecked={client.unsupportedCountryOnboardingEnabled}
              name="International"
              onSuccess={invalidateClient}
              prepare={(value) => ({
                id: client.id,
                body: { unsupported_country_onboarding_enabled: value },
              })}
              updateEntity={putClient}
            />
          </SettingsRow>
          <SettingsRow label="Voicemail">
            {client.voicemailUrl && <AudioPlayer src={client.voicemailUrl} />}
            <Dropzone accept={{ 'audio/mp3': [], 'audio/mpeg': [] }} noDrag onFileAccepted={onFileAccepted}>
              <Button icon={<UploadOutlined />}>Upload</Button>
            </Dropzone>
          </SettingsRow>
        </SettingsRows>
      </SettingsCard>
      <EditOnboardingMessagesList client={client} messageLabels={EDITABLE_MESSAGES} title="Fan onboarding messages" />
      {client.onboardingMessages.some((m: any) => !EDITABLE_MESSAGES.includes(m.label)) && (
        <EditOnboardingMessagesList
          actions={
            <Button
              icon={<PlusOutlined />}
              onClick={() => setIsCreateImportOnboardingMessageModalOpen(true)}
              type="primary"
            >
              New Import Onboarding Message
            </Button>
          }
          client={client}
          messageLabels={client.onboardingMessages
            .map(({ label }: { label: string }) => label)
            .filter((label: string) => !EDITABLE_MESSAGES.includes(label))}
          title="Import onboarding messages"
        />
      )}
      {isCreateImportOnboardingMessageModalOpen && (
        <Form
          initialValues={{
            label: undefined,
            text: '',
            templateType: undefined,
          }}
          onSubmit={handleCreateImportOnboardingMessage}
          render={({ handleSubmit, submitting, valid, values }) => (
            <Modal
              // the z-index must be less than 5 for the popover to show on top
              // of the modal.
              confirmLoading={submitting}
              okButtonProps={{ disabled: !valid }}
              okText="Add"
              onCancel={() => setIsCreateImportOnboardingMessageModalOpen(false)}
              onOk={handleSubmit}
              open={isCreateImportOnboardingMessageModalOpen}
              title="New Import Onboarding Message"
              width={800}
              zIndex={4}
            >
              <form onSubmit={handleSubmit}>
                <Field name="label" validate={required}>
                  {({ input }) => (
                    <Layout width="100%">
                      <Select
                        aria-label="Select Onboarding Message Type"
                        onChange={(value: string) => input.onChange({ target: { value } })}
                        options={onboardingMessageLabelOptions}
                        placeholder="Import Message Label"
                        size="large"
                        style={{ width: '100%' }}
                        virtual={false}
                      />
                    </Layout>
                  )}
                </Field>
                {isWhatsAppMessageLabel(values.label) && (
                  <Field name="templateType" validate={required}>
                    {({ input }) => (
                      <Layout marginTop="8px">
                        <TemplateTypeSelect
                          onChange={(value) => input.onChange({ target: { value } })}
                          size="large"
                          value={input.value}
                        />
                      </Layout>
                    )}
                  </Field>
                )}
                <MessageField
                  chopperDomain={chopperDomain}
                  label="Message"
                  name="text"
                  setShouldShortenLinks={setShouldShortenLinks}
                  shouldShortenLinks={shouldShortenLinks}
                  validators={[required]}
                />
              </form>
            </Modal>
          )}
        />
      )}
    </SettingsPage>
  )
}
